
import { Picture, Source } from "~/components/ui/Picture"
import { SectionProps } from "~/types/deco"
import { DepartmentBanners } from "~/loaders/DepartmentBanners"
import { isSchedulingActive } from "~/sdk/contentScheduling"
import { extractTitleFromPath } from "~/sdk/format"
import { SITE } from "~/constants"

export interface SearchBannerProps {
  /**
   * @title Banners
   */
  items: DepartmentBanners[]
  /**
   * @title LCP
   * @description Essa propriedade não deve ser marcada se nesta página houver outra imagem com LCP ativo.
   */
  isLCP: boolean
}

/**
 * @title Search Banner v2
 */
const SearchBanner = (
  {
    TITLE,
    IMAGE_SRC,
    IMAGE_WIDTH,
    IMAGE_HEIGHT,
    IMAGE_ALT,
    LOADING,
    FETCH_PRIORITY,
    BANNER_CONTENT,
  }: SectionProps<typeof loader>,
) => {
  if (!IMAGE_SRC) {
    return (
      <h1 className='max-w-[1810px] mx-auto p-4 text-[#ff8a3d] text-[26px] leading-none font-medium'>
        {TITLE}
      </h1>
    )
  }

  return (
    <div className='m-4 mb-0 lg:m-5 lg:mb-0 min-[1810px]:mx-0'>
      <div className='relative flex items-center w-full max-w-[1800px] mx-auto'>
        <Picture className='w-full h-full'>
          <Source
            media='(max-width: 1024px)'
            src={IMAGE_SRC}
            width={IMAGE_WIDTH}
            height={IMAGE_HEIGHT}
            fetchPriority={FETCH_PRIORITY}
          />
          <Source
            media='(min-width: 1025px)'
            src={IMAGE_SRC}
            width={IMAGE_WIDTH}
            height={IMAGE_HEIGHT}
            fetchPriority={FETCH_PRIORITY}
          />
          <img
            className='object-cover w-full h-auto'
            src={IMAGE_SRC}
            alt={IMAGE_ALT}
            loading={LOADING}
            width={IMAGE_WIDTH}
            height={IMAGE_HEIGHT}
            onError={(e) => {
              console.error('Erro ao carregar imagem', e)
              const closestDiv = e.currentTarget.closest('div')
              if (closestDiv) {
                closestDiv.style.display = 'none'
              }
            }}
          />
        </Picture>
        {BANNER_CONTENT && (
          <div
            className='absolute left-0 top-1/2 -translate-y-1/2 mx-[5%] sm:mx-[9%] lg:max-w-[300px] [&_h1]:font-medium text-sm text-white [&_h1]:mb-2 [&_h1]:text-[#ff8a3d] [&_h1]:text-[26px] [&_h1]:leading-none'
            dangerouslySetInnerHTML={{ __html: BANNER_CONTENT }}
          />
        )}
      </div>
    </div>
  )
}

/**
 * @description Loader for Search Banner v2, returns the props for the component.
 */
export function loader(
  props: SearchBannerProps & { __pageUrl?: string; isMobile?: boolean },
) {
  const isMobile = props.isMobile ?? false
  const isLCP = props.isLCP ?? false
  const currentURL = new URL(props.__pageUrl ?? "/", SITE.URL)
  const departmentItem = props.items?.find(({ matcher }) =>
    currentURL.pathname.toLowerCase() === matcher.toLowerCase()
  )
  const departmentItemBanner = departmentItem?.banner
  const banner = departmentItemBanner && isSchedulingActive(departmentItemBanner.scheduling)
    ? departmentItemBanner
    : undefined

  const componentProps = {
    TITLE: extractTitleFromPath(currentURL.pathname),
    IMAGE_SRC: isMobile ? banner?.image?.mobile : banner?.image?.desktop,
    IMAGE_WIDTH: isMobile ? 382 : 1920,
    IMAGE_HEIGHT: isMobile ? 142 : 250,
    IMAGE_ALT: banner?.alt ?? 'Search Banner',
    PRELOAD: isLCP,
    LOADING: isLCP ? 'eager' : 'lazy' as 'eager' | 'lazy',
    FETCH_PRIORITY: isLCP ? 'high' : 'low' as 'high' | 'low' | 'auto',
    BANNER_CONTENT: banner?.content,
  }

  return componentProps
}

/**
 * @description Loading fallback for Search Banner v2.
 */
export const LoadingFallback = () => {
  return (
    <div className='m-4 mb-0 lg:m-5 lg:mb-0 min-[1810px]:mx-0'>
      <div className='w-full h-full max-w-[1800px] mx-auto bg-gray-200 aspect-[768/287] lg:aspect-[1920/250]'>
      </div>
    </div>
  )
}

export default SearchBanner
